function debounce(fn, wait = 16) {
  let timerId;

  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      fn(...args);
    }, wait);
  };
}

function throttle(fn, wait = 50) {
  let lastTime = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastTime >= wait) {
      lastTime = now;
      fn(...args);
    }
  };
}

const easeOutQuint = x => {
  return 1 - Math.pow(1 - x, 5);
}

const VERIFY_HOOKS_TYPE = {
  DEFAULTS: '$node'
}

const hooksOf = (obj = {}, type = 'DEFAULTS') => {
  return Object.hasOwnProperty.call(obj, VERIFY_HOOKS_TYPE[type]);
}

function observable(obj) {
  let state = obj;
  let subscribers = [];
  let pending = null;

  // subscribe에 저장된 함수를 호출하지 않고 state만 업데이트 하는 함수
  const update = nextState => {
    switch (typeof nextState) {
      case "function":
        state = diff(nextState(getState()));
      default:
        state = diff(nextState);
    }
  }

  const fn = () => {
    subscribers.forEach(observer => {
      observer(getState())
    })
  };

  const diff = next => {
    if(Object.keys(next).length <= 0) return obj;

    for(let k in next) {
      if(!Object.prototype.hasOwnProperty.call(state, k)) {
        delete next[k]; 
      }
    }
    return {
      ...state,
      ...next
    };
  }

  const getState = () => {
    return {
      ...state
    }
  }

  const _setState = nextState => {
    update(nextState);
    fn();
  }

  const debouncedSetState = debounce(() => {
    const final = typeof pending === "function" ? pending(getState()) : pending;
    _setState(final);
    pending = null;
  }, 16);

  const setState = nextState => {
    pending = typeof nextState === "function"
      ? s => nextState(typeof pending === "function" ? pending(s) : getState())
      : { ...(typeof pending === "function" ? pending(getState()) : pending || {}), ...nextState };
  
    debouncedSetState();
  };

  const subscribe = observer => {
    subscribers.push(observer);

    return () => {
      const index = subscribers.findIndex(o => o === observer);
      subscribers = [
        ...subscribers.slice(0, index),
        ...subscribers.slice(index + 1)
      ]
    }
  }



  // @@INIT
  setState(obj)

  return {
    getState,
    setState,
    subscribe,
    update
  }
}

const useTabs = ({ ref, state = {} }) => {
  const _stateFactory = () => ({
    index: -1,
    $current: null
  })
 
  const _$nodeFactory = ref => {
    return {
      all: Array.from(ref),
      parent: ref[0].parentNode
    }
  }

  const $node = _$nodeFactory(ref);
  const { getState, setState, subscribe, update } = observable(Object.assign(_stateFactory(), state));

  
  return {
    get state() {
      return getState();
    },
    set state(next) {
      setState(next);
    },
    $node,
    subscribe,
    update
  }
}

const useSticky = ({ ref, state = {} }) => {
  const _stateFactory = () => ({ isSticky: false })
  const { getState, setState, subscribe } = observable(Object.assign(_stateFactory(), state));

  return {
    get state() {
      return getState()
    },
    set state(next) {
      setState(next);
    },
    $node: ref,
    subscribe
  }
}

const useScroll = ({ ref, state = {} }) => {
  const _stateFactory = $node => {
    switch($node) {
      case window:
        return {
          top: window.scrollY || document.documentElement.scrollTop,
          height: window.innerHeight,
          isBeginning: (window.scrollY || document.documentElement.scrollTop) <= 0, 
          isEnd: (window.scrollY || document.documentElement.scrollTop) >= document.documentElement.scrollHeight - window.innerHeight 
        }
      default:
        return {
          top: ref.scrollTop,
          height: ref.offsetHeight,
          isBeginning: ref.scrollTop <= 0,
          isEnd: ref.scrollTop >= (ref.scrollHeight - ref.offsetHeight)
        }
    }
  }
  const $node = ref || window;
  
  const { getState, setState, subscribe } = observable(Object.assign(_stateFactory($node), state));

  const handler = () => {
    setState(_stateFactory($node));
  };

  $node.addEventListener("scroll", throttle(handler, 50));

  return {
    $node,
    get state() {
      return getState()
    },
    subscribe,
    unbind: () => $node.removeEventListener("scroll", handler)
  }
}

const useImagesLoaded = (ref = document) => {
  const _stateFactory = loaded => ({ loaded })
  const { getState, setState, subscribe } = observable(_stateFactory(false));
  let timerId = null;

  const _onLoad = () => {
    setState(_stateFactory(true));
  }

  const onLoad = () => {
    cancelAnimationFrame(timerId);
    timerId = requestAnimationFrame(_onLoad);
  }

  if(ref.querySelectorAll("img").length > 0) {
    Array.from(ref.querySelectorAll("img")).forEach($e => {
      $e.complete && onLoad();
      $e.addEventListener("load", onLoad, { once: true });
      $e.addEventListener("error", onLoad, { once: true });
    })
  } else {
    _onLoad()
  }

  return {
    get state() {
      return getState()
    },
    subscribe
  }
}

const useSlider = ({ listRef, prevRef, nextRef, state = {}}) => {
  const _stateFactory = x => ({ x, index: 0, isBeginning: true, isEnd: false });
  const _nodeFactory = listRef => ({
    children: Array.from(listRef.children),
    self: listRef,
    prevBtn: prevRef,
    nextBtn: nextRef
  })

  const { getState, setState, subscribe, update } = observable(Object.assign(_stateFactory(0), state));
  const $node = _nodeFactory(listRef);


  let timerId = null;

  const limit = {
    start: 0,
    end: -Infinity
  }


  const _setState = ({ x: dx, index, isBeginning, isEnd }) => {
    if(timerId) cancelAnimationFrame(timerId);
    const { x: sx } = getState();

    if(sx + dx < limit.end) {
      dx = limit.end - sx 
    }

    if(sx + dx >= limit.start) {
      dx = sx * -1;
    }

    const then = Date.now();
    const DURATION = 300;

    function draw() {
      const now = Date.now();
      const elapsed = now - then;

      const amount = dx * Math.min(easeOutQuint(elapsed  / DURATION), 1);
      const nx = sx + amount;

      setState({
        index,
        x: nx,
        isBeginning,
        isEnd
      })

      if(elapsed < DURATION) {
        timerId = requestAnimationFrame(draw)
      } else {
        timerId = null;
      }
    }
    timerId = requestAnimationFrame(draw);
  }

  useImagesLoaded($node.self).subscribe(state => {
    if(state.loaded) {
      const total = $node.children.reduce((a, c) => {
        a += c.offsetWidth;
        return a;
      }, 0);
      limit.end = (total - $node.self.offsetWidth) * -1;
    };
  })

  
  prevRef?.addEventListener("click", e => {
    const { index, isBeginning, x: sx } = getState();

    if(index - 1 < 0 || isBeginning) return;
    
    const _$prev = $node.children.find(($n, i) => {
      return (index - 1) === i
    });

    const offsetLeft = _$prev.getBoundingClientRect().left - $node.self.getBoundingClientRect().left;

    _setState({
      x: (offsetLeft * -1) - sx,
      index: index - 1,
      isBeginning: index - 1 === 0,
      isEnd: false,
    })

  });

  nextRef?.addEventListener("click", e => {
    const { index, isEnd, x: sx } = getState();

    if(index + 1 > $node.children.length || isEnd) return;

    const _$next = $node.children.find(($n, i) => {
      return (index + 1) === i
    });

    const offsetLeft = _$next.getBoundingClientRect().left - $node.self.getBoundingClientRect().left;

    _setState({
      x: (offsetLeft * -1) - sx,
      index: index + 1,
      isBeginning: false,
      isEnd: index + 1 === $node.children.length - 1
    })
  })
  

  subscribe(({ x, isBeginning, isEnd }) => {
    $node.self.style.transform = `translateX(${x}px)`;

    if(isBeginning) {
      prevRef?.setAttribute("disabled", true);
      nextRef?.removeAttribute("disabled")
    } else if(isEnd) {
      prevRef?.removeAttribute("disabled");
      nextRef?.setAttribute("disabled", true)
    } else {
      prevRef?.removeAttribute("disabled");
      nextRef?.removeAttribute("disabled");
    }
  });


  return {
    get state() {
      return getState()
    },
    set state(next) {
      _setState(next)
      return;
    },
    $node,
    subscribe,
    update
  }
}

const useStickyTabs = ({ scrollRef, stickyRef, tabsRef, state = { tabs: {}, sticky: {}, scroll: {} } }) => {
  const scroller = useScroll({ ref: scrollRef, state: state.scroll });
  const sticky = useSticky({ ref: stickyRef, state: state.sticky });
  const tabs = useTabs({ ref: tabsRef, state: state.tabs || { index: -1, $current: null } });
  const OFFSET =  {
    STICKY: Infinity,
  }


  useImagesLoaded().subscribe(state => {
    if(state.loaded) {
      OFFSET.STICKY = Math.floor(sticky.$node.parentNode.getBoundingClientRect().top + scroller.state.top);
    }
  })

  scroller.subscribe(state =>   {
    sticky.state = {
      isSticky: state.top >= OFFSET.STICKY,
    };

    let matched = false;

    for (let index = 0; index < tabs.$node.all.length; index++) {
      const $node = tabs.$node.all[index];
      const selector = $node.dataset.anchor;
      const $anchor = document.querySelector(selector);


      if (!$anchor) {
        console.warn(`⚠️ anchor not found for selector:`, selector);
        continue;
      }

      const rect = $anchor.getBoundingClientRect();
      const offsetTop = state.top + rect.top - sticky.$node.offsetHeight;
      const offsetBottom = state.top + rect.bottom - sticky.$node.offsetHeight;

      if (state.top < offsetBottom && state.top + state.height > offsetTop) {

        tabs.state = {
          index,
          $current: tabs.$node.all[index],
        };
        matched = true;
        break;
      }
    }
    

    // TODO: 리팩토링 필요
    const isBottom = state.top + state.height >= document.documentElement.scrollHeight;

    if (!matched) {
      if (isBottom) {
        const lastIndex = tabs.$node.all.length - 1;
        tabs.state = {
          index: lastIndex,
          $current: tabs.$node.all[lastIndex],
        };
      } else if (tabs.state.index !== -1) {
        tabs.state = { index: -1, $current: null };
      }
    }
  });

  sticky.subscribe(state => {
    if(state.isSticky) {
      sticky.$node.classList.add("fixed");
      sticky.$node.parentNode.style.cssText = `height:${tabs.$node.parent.offsetHeight}px`
    } else {
      sticky.$node.classList.remove("fixed");
      sticky.$node.parentNode.removeAttribute("style");
    }
  });

  tabs.$node.parent.addEventListener("click", e => {
    e.preventDefault();
    const $node = tabs.$node.all.find($t => $t === e.target.closest("[role='tab']"));

    if($node && $node.contains(e.target)) {
      const { top } = document.querySelector($node.dataset.anchor).getBoundingClientRect();

      window.scrollTo({
        top: Math.floor((top + scroller.state.top)) - sticky.$node.offsetHeight,
        behavior: 'smooth'
      })
    }
  })

  tabs.subscribe(state => {
    tabs.$node.all.forEach(($t, i) => i === state.index ? $t.classList.add("active") : $t.classList.remove("active"));
  })

  return {
    scroller,
    tabs,
    sticky
  }
}

const useSliderTabs = ({ tabsRef, sliderRef, state = { tabs: {}, slider: {} }}) => {
  const tabs = useTabs({ ref: tabsRef, state: state.tabs });
  
  const slider = useSlider({
    listRef: tabs.$node.parent,
    ...sliderRef,
    state: state.slider
  });

  tabs.$node.parent.addEventListener("click", e => {
    e.preventDefault();
    const $t = slider.$node.children.find($t => $t === e.target.closest("[role='tab']"));

    if($t && $t.contains(e.target)) {
      const sx = slider.state.x;
      const offsetLeft = $t.getBoundingClientRect().left - slider.$node.self.getBoundingClientRect().left;
      const index = slider.$node.children.findIndex(t => $t === t)

      slider.state = {
        x: (offsetLeft * -1) - sx,
        index,
        isBeginning: index === 0,
        isEnd: index === slider.$node.children.length - 1,
      }
    }
  });

  tabs.subscribe(state => {
    tabs.$node.all.forEach(($t, i) => i === state.index ? $t.classList.add("active") : $t.classList.remove("active"));
  })

  slider.subscribe(state => {
    tabs.state = {
      index: state.index,
      $current: tabs.$node.all.find((t, i) => i === state.index)
    }
  });

  return {
    slider,
    tabs,
  }
}

const useSwiperTabs = ({ tabsRef, state = { tabs: {}, swiper: {} } }) => {
  const tabs = useTabs({ ref: tabsRef, state: state.tabs });
  const swiper = observable(new Swiper(tabs.$node.parent.parentNode, {
    ...state.swiper,
  }));

  tabs.$node.parent.addEventListener("click", e => {
    const $current = tabs.$node.all.find($t => $t.contains(e.target));
    const index = tabs.$node.all.findIndex($t => $t === $current);
    tabs.state = {
      $current,
      index
    }
  })

  tabs.subscribe(({ index }) => {
    tabs.$node.all.forEach(($t, i) => i === index ? $t.classList.add("active") : $t.classList.remove("active"));
  });


  return {
    tabs,
    swiper,
  }
}

const useSliderStickyTabs = ({ scrollRef, stickyRef, tabsRef, sliderRef, state = { tabs: {}, sticky: {}, scroll: {}, slider: {} }, ...args }) => {
  const { tabs, scroller, sticky } = useStickyTabs({ scrollRef, stickyRef, tabsRef, state, ...args });

  const slider = useSlider({
    listRef: tabs.$node.parent,
    state: state.slider,
    ...sliderRef,
    ...args
  })

  tabs.subscribe(({ $current, index }) => {
    if(index < 0) return;

    const sx = slider.state.x;
    const offsetLeft = $current.getBoundingClientRect().left - slider.$node.self.getBoundingClientRect().left;

    slider.state = {
      x: (offsetLeft * -1) - sx,
      index,
      isBeginning: index === 0,
      isEnd: index === slider.$node.children.length - 1,
    }
  });

  slider.subscribe(({ index }) => {
    tabs.update({
      index,
      $current: tabs.$node.all[index]
    })
  });

  return {
    tabs,
    slider,
    scroller,
    sticky
  }
}

const useSwiperStickyTabs = ({ scrollRef, stickyRef, tabsRef, sliderRef, state = { tabs: {}, sticky: {}, scroll: {}, swiper: {} }, ...args }) => {
  const { tabs, scroller, sticky } = useStickyTabs({ scrollRef, stickyRef, tabsRef, state, ...args });
  const swiper = observable(new Swiper(tabs.$node.parent.parentNode, {
    ...state.swiper,
  }));

  return {
    tabs,
    swiper,
    scroller,
    sticky
  }
}